import type {
  ModerationCategory,
  ModerationInputKind,
  ModerationSubjectType,
  ModerationSurface,
  ReportReason,
  SurfaceBan,
} from "@pax/api-contract/moderation/v1alpha1";

/** Each reason a user can give, in the moderation copy's words. */
export const REPORT_REASONS: Record<ReportReason, { title: string; description: string }> = {
  self_harm: {
    title: "Self-harm",
    description: "This content is depicting or glorifying self-harm.",
  },
  directing_self_harm: {
    title: "Directing self-harm",
    description: "This content encourages others to harm themselves.",
  },
  sexual_content: {
    title: "Sexual",
    description: "This content is sexual or suggestive (PG-13+).",
  },
  "graphic/glorifying_atrocities": {
    title: "Graphic or glorifying atrocities",
    description:
      "This content is graphic (eg, sensory detail about violence) or glorifies an atrocity.",
  },
  graphic: {
    title: "Graphic",
    description: "This content is graphic (eg, sensory detail about violence).",
  },
  glorifying_atrocities: {
    title: "Glorifying atrocities",
    description: "This content glorifies an atrocity.",
  },
  identity_hate: {
    title: "Hate speech",
    description:
      "This content involves hate speech or harmful rhetoric/depictions against an identity group.",
  },
  identity_ingame_violence: {
    title: "Hate actions",
    description:
      "This content involves discriminatory or hateful actions against an identity group.",
  },
  personal_threats: {
    title: "Personal threats",
    description: "This content involves a realistic threat against a real person.",
  },
  harassment: {
    title: "Harassment",
    description: "This content involves harassment against me or someone else.",
  },
  "pii/off_platform": {
    title: "Personal information, doxing, or links",
    description:
      "This content involves providing information that could result in someone being contacted off-platform.",
  },
  misrepresented_metadata: {
    title: "Misrepresented information",
    description:
      "This content has a misleading label/text/title/etc that doesn't match the actual content.",
  },
  spam: { title: "Spam", description: "This content involves spam." },
  other: { title: "Other", description: "Please describe." },
};

export const REPORT_COPY = {
  cta: "Report",
  reasonPrompt: "Why are you reporting this?",
  explanationPrompt: "Please tell us why you believe this content is unsafe or violates our TOS.",
  ownershipTitle: "Copyright",
  ownershipDescription: "This content uses work that belongs to me or someone else.",
  ownership: "Please go read our TOS to find our DMCA contact so we can assist you.",
  submitted: "We've received your report. Thanks!",
  noOutcomePromise:
    "Thank you for reporting. We don't follow up with reporters, but we will investigate the content you've reported.",
  duplicate: (noun: string) => `You've already reported this ${noun}.`,
};

export const SUBJECT_NOUNS: Record<ModerationSubjectType, string> = {
  asset: "asset",
  collection: "collection",
  preset: "preset",
  profile: "profile",
  comment: "comment",
};

/** What each category covers, for moderators, and the surfaces it applies on. */
export const CATEGORIES: Record<
  ModerationCategory,
  { title: string; description: string; surfaces: readonly ModerationSurface[] }
> = {
  self_harm: {
    title: "Self-harm",
    description:
      "Depiction or glorification of self-harm, or self-directed comments about it. A welfare response, not an offense.",
    surfaces: ["assets", "presets", "profile", "social", "live_game"],
  },
  directing_self_harm: {
    title: "Directing self-harm",
    description: "Encouraging another person to harm themselves.",
    surfaces: ["assets", "presets", "profile", "social", "live_game"],
  },
  sexual_content: {
    title: "Sexual",
    description:
      "Genitals, revealing clothing, sex. All sexual content belongs here, whatever age is depicted.",
    surfaces: ["assets", "presets", "profile", "social", "live_game"],
  },
  "graphic/glorifying_atrocities": {
    title: "Graphic or glorifying atrocities",
    description:
      "Realistic wounds, dead people, screaming audio, graphic drug use, or celebrating horrible acts.",
    surfaces: ["profile", "social", "live_game"],
  },
  graphic: {
    title: "Graphic",
    description: "Realistic wounds, dead people, screaming audio, graphic drug use.",
    surfaces: ["assets", "presets"],
  },
  glorifying_atrocities: {
    title: "Glorifying atrocities",
    description: "Celebrating atrocities, violence, or people associated with them.",
    surfaces: ["assets", "presets"],
  },
  identity_hate: {
    title: "Hate speech",
    description: "Slurs, hate speech, hate symbols, racist caricatures.",
    surfaces: ["assets", "presets", "profile", "social", "live_game"],
  },
  identity_ingame_violence: {
    title: "Hate actions",
    description: "Game actions or authored rules enacting harm on a real-world group.",
    surfaces: ["presets", "live_game"],
  },
  personal_threats: {
    title: "Personal threats",
    description: "Real-world violence threatened at a particular real-world target.",
    surfaces: ["assets", "presets", "profile", "social", "live_game"],
  },
  harassment: {
    title: "Harassment",
    description: "Bullying, insults, or persistent unwanted attention at a specific person.",
    surfaces: ["assets", "presets", "profile", "social", "live_game"],
  },
  "pii/off_platform": {
    title: "Personal info or links",
    description: "Personal identifiers, contact info, doxing, and links of any kind.",
    surfaces: ["assets", "presets", "profile", "social", "live_game"],
  },
  prompt_injection: {
    title: "Prompt injection",
    description: "Text aimed at getting the AI to bypass moderation.",
    surfaces: ["assets", "presets", "profile", "social", "live_game"],
  },
  health_risk: {
    title: "Health risk",
    description: "Flashing, strobe, loudness spikes.",
    surfaces: ["assets", "presets"],
  },
  misrepresented_metadata: {
    title: "Misrepresented information",
    description:
      "A title, tags, or description that don't match the content, or unmarked AI generation.",
    surfaces: ["assets", "presets"],
  },
  spam: {
    title: "Spam",
    description: "Relentless uploads, messages, and the like that make the site worse.",
    surfaces: ["assets", "presets", "profile", "social", "live_game"],
  },
  appropriate: {
    title: "Appropriate",
    description: "Standard content with nothing wrong in it.",
    surfaces: ["assets", "presets", "profile", "social", "live_game"],
  },
};

/** How extreme content is within a category, from 1 to 5. */
export const SEVERITIES = [
  { severity: 1, label: "Unlikely to bother reasonable adults" },
  { severity: 2, label: "Could bother some people" },
  { severity: 3, label: "Would disturb many" },
  { severity: 4, label: "Would disturb almost everyone" },
  { severity: 5, label: "Crosses the category's bright line" },
] as const;

/** What a ban on each surface takes away: the copy's `{scope}`. */
const BAN_SCOPES: Record<SurfaceBan["surface"], string> = {
  live_game: "multiplayer",
  profile: "profile editing",
  social: "social features like comments and notes",
  assets: "assets",
  presets: "presets",
};

/**
 * How the refusal copy names an input: `{input}`, `{inputs}`, and the surface whose ban the
 * `{scope}` names.
 */
export interface InputWords {
  input: string;
  inputs: string;
  surface: ModerationSurface;
  scope: string;
}

const named = (input: string, inputs: string, surface: ModerationSurface): InputWords => ({
  input,
  inputs,
  surface,
  scope: BAN_SCOPES[surface],
});

/** A preset judged as a whole, which `whole` alone doesn't tell apart from an asset's draft. */
export const PRESET_WORDS = named("preset", "presets", "presets");

export const INPUT_WORDS: Record<ModerationInputKind, InputWords> = {
  profile_username: named("username", "usernames", "profile"),
  profile_display_name: named("display name", "display names", "profile"),
  profile_bio: named("bio", "bios", "profile"),
  profile_region: named("region", "regions", "profile"),
  profile_avatar: named("profile photo", "profile photos", "profile"),
  profile_banner: named("banner", "banners", "profile"),
  asset_media: named("file", "files", "assets"),
  asset_text: named("description, title, or tag", "descriptions, titles, or tags", "assets"),
  collection_text: named("title or description", "titles or descriptions", "assets"),
  preset_image: named("image", "images", "presets"),
  preset_title: named("title", "titles", "presets"),
  comment_body: named("comment", "comments", "social"),
  live_chat: named("message", "messages", "live_game"),
  live_action: named("action", "actions", "live_game"),
  live_rule: named("rule", "rules", "live_game"),
  live_role: named("role name", "role names", "live_game"),
  live_title: named("game title", "game titles", "live_game"),
  whole: named("draft", "drafts", "assets"),
};

/** Where a user in crisis is pointed: a directory of helplines in every country. */
export const CRISIS_RESOURCES_URL = "https://findahelpline.com";

const warned = (flagged: string) => (words: InputWords) =>
  `Your ${words.input} was flagged as ${flagged}. You may be banned if you submit more ${words.inputs} which violate our TOS.`;
const cautioned = (flagged: string) => (words: InputWords) =>
  `Your ${words.input} was flagged as ${flagged}. Repeated violations of our TOS may result in a ban from ${words.scope}.`;

/**
 * Each category's inline warning, and the pop-up a bigger refusal adds. `self_harm`'s pop-up is the
 * welfare message, which `RefusalWarning` draws with its link to crisis support.
 */
export const REFUSAL_WARNINGS: Record<
  ModerationCategory,
  { small: string; title: string; body: (words: InputWords) => string }
> = {
  self_harm: {
    small: "Refused by auto-mod (welfare concern).",
    title: "Help is available.",
    body: words =>
      `Based on your last ${words.input}, our system flagged that you may be in crisis.`,
  },
  directing_self_harm: {
    small: "Refused by auto-mod (encouraging self-harm).",
    title: "Warning.",
    body: warned("encouraging self-harm"),
  },
  sexual_content: {
    small: "Refused by auto-mod (sexual content).",
    title: "Warning.",
    body: warned("sexual"),
  },
  "graphic/glorifying_atrocities": {
    small: "Refused by auto-mod (graphic/glorifying atrocities).",
    title: "Careful.",
    body: cautioned("graphic and/or glorifying atrocities"),
  },
  graphic: {
    small: "Refused by auto-mod (graphic).",
    title: "Careful.",
    body: cautioned("graphic"),
  },
  glorifying_atrocities: {
    small: "Refused by auto-mod (glorifying atrocities).",
    title: "Careful.",
    body: cautioned("glorifying atrocities"),
  },
  identity_hate: {
    small: "Refused by auto-mod (hate speech).",
    title: "Careful.",
    body: cautioned("hate speech"),
  },
  identity_ingame_violence: {
    small: "Refused by auto-mod (identity-based violence).",
    title: "Careful.",
    body: cautioned("extreme or hateful actions against an identity group (not a nation)"),
  },
  personal_threats: {
    small: "Refused by auto-mod (personal threats).",
    title: "Careful.",
    body: cautioned("a personal threat against another player"),
  },
  harassment: {
    small: "Refused by auto-mod (harassment).",
    title: "Careful.",
    body: cautioned("harassment"),
  },
  "pii/off_platform": {
    small: "Refused by auto-mod (personal info or moving off-platform).",
    title: "Warning.",
    body: warned("personal information or as off-platform movement"),
  },
  prompt_injection: {
    small: "Refused by auto-mod (trying to steer the auto-mod).",
    title: "Careful.",
    body: cautioned("trying to steer the auto-mod"),
  },
  health_risk: {
    small: "Refused by auto-mod (health risk).",
    title: "Careful.",
    body: cautioned("a potential health risk (strobe, etc.)"),
  },
  misrepresented_metadata: {
    small: "Refused by auto-mod (inaccurate title/tags/text).",
    title: "Careful.",
    body: cautioned("being misleading (the text/title/tags don't match the file)"),
  },
  spam: {
    small: "Refused by auto-mod (spam).",
    title: "Careful.",
    body: cautioned("spam"),
  },
  appropriate: {
    small: "Refused by auto-mod.",
    title: "Careful.",
    body: words => `Your ${words.input} wasn't saved.`,
  },
};

export const APPEAL_COPY = {
  cta: "Appeal",
  submitted: "Your appeal has been received.",
  unavailableSexualContent: "Sorry, we don't allow non-text sexual content to be appealed.",
  unavailable: "This cannot be appealed.",
  oneAtATime: (words: InputWords) => `An appeal is already open for this ${words.input}!`,
  escalatedHuman:
    "Your appeal has been received. It will be judged one final time by a moderator on our team.",
  upheldAi:
    "Sorry, our automod rejected your appeal. You can appeal one more time to a human moderator.",
  upheldHuman: "Sorry, your appeal was rejected by a moderator. You cannot appeal again.",
  withdrawn: "You withdrew your appeal.",
  overturnedPreset: "Congrats! Your appeal succeeded. Publish it again and it goes through.",
  overturnedBan:
    "Congrats! Your appeal succeeded. Your ban should now be lifted (it has also been removed from your record).",
  escalateCta: "Appeal to a moderator",
  withdrawCta: "Withdraw appeal",
};

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

const counted = (count: number, unit: string) => `${count} ${unit}${count === 1 ? "" : "s"}`;

/** A span of time in the largest unit that fits, rounded so it never reads as zero. */
function span(milliseconds: number): string {
  if (milliseconds >= DAY) return counted(Math.round(milliseconds / DAY), "day");
  if (milliseconds >= HOUR) return counted(Math.ceil(milliseconds / HOUR), "hour");
  return counted(Math.max(1, Math.ceil(milliseconds / 60_000)), "minute");
}

const categoryList = (categories: readonly ModerationCategory[]) =>
  new Intl.ListFormat("en", { type: "conjunction" }).format(
    categories.map(category => CATEGORIES[category].title.toLowerCase()),
  );

const dayFormat = new Intl.DateTimeFormat("en", { month: "short", day: "numeric" });

/** What a ban that can be appealed says about it: the copy's surface specific appeal message. */
const BAN_APPEAL_LINE = "If you think it was a mistake, you can appeal it.";

/** The account-wide notices when points near a ban and when a ban lands. */
export const BAN_COPY = {
  approachingTitle: "Caution.",
  approaching: (categories: readonly ModerationCategory[]) =>
    `Your account has been flagged for ${categoryList(categories)} violations. Further violations will result in a ban.`,
  bannedTitle: "Banned.",
  banned: (ban: SurfaceBan, now = Date.now()) => {
    const length = ban.endsAt
      ? `for ${span(Date.parse(ban.endsAt) - Date.parse(ban.startsAt))}`
      : "permanently";
    // A ban a later ruling caused names the day of the violations, so it doesn't seem to come from nowhere.
    const late =
      Date.parse(ban.earnedAt) < Date.parse(ban.startsAt)
        ? ` on ${dayFormat.format(new Date(ban.earnedAt))}`
        : "";
    const reason =
      ban.categories.length > 0
        ? ` because of repeated ${categoryList(ban.categories)} violations${late}`
        : "";
    const appeal = ban.appealable ? ` ${BAN_APPEAL_LINE}` : "";
    const expiry = ban.endsAt
      ? ` ${ban.appealable ? "Otherwise, your" : "Your"} ban will expire in ${span(Date.parse(ban.endsAt) - now)}.`
      : "";
    return `Your account was banned from ${BAN_SCOPES[ban.surface]} ${length}${reason}.${appeal}${expiry}`;
  },
};

const expiryFormat = new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short" });
const banExpiry = (endsAt: string | null) =>
  endsAt ? ` Ban expires: ${expiryFormat.format(new Date(endsAt))}.` : "";

const liveAppeal = (ban: { appealable: boolean }) => (ban.appealable ? ` ${BAN_APPEAL_LINE}` : "");

/**
 * What a player banned from live games is told inside a game: when the ban lands mid-session, and
 * when they come back while it lasts.
 */
export const LIVE_BAN_COPY = {
  demotedSpectator: (ban: { endsAt: string | null; appealable: boolean }) =>
    `You've been banned from ${BAN_SCOPES.live_game}. You are now a spectator.${banExpiry(ban.endsAt)}${liveAppeal(ban)}`,
  connectRefused: (ban: { endsAt: string | null; appealable: boolean }) =>
    `You've been banned from ${BAN_SCOPES.live_game}. You are a spectator.${banExpiry(ban.endsAt)}${liveAppeal(ban)}`,
};

export const automodUnavailable = (inputs: string) =>
  `We're sorry. Our automod system is down, so we can't allow any ${inputs}. We are working to fix this ASAP.`;

export const INPUT_NAMES: Record<ModerationInputKind, string> = {
  profile_username: "Username",
  profile_display_name: "Display name",
  profile_bio: "Bio",
  profile_region: "Region",
  profile_avatar: "Profile photo",
  profile_banner: "Banner",
  asset_media: "File",
  asset_text: "Title, description, and tags",
  collection_text: "Title and description",
  preset_image: "Image",
  preset_title: "Title",
  comment_body: "Comment",
  live_chat: "Chat message",
  live_action: "Action",
  live_rule: "Rule",
  live_role: "Role name",
  live_title: "Game title",
  whole: "As a whole",
};
